import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Fade,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sidebarClose } from "../../../redux/reducers/sidebarReducer";
import Htag from "../../typography/Htag";
import CreateEditBorrowerBankInfoForm from "../../../forms/CreateEditBorrowerBankInfoForm";
import UpdateLoanConditionForm from "../../../forms/UpdateLoanConditionForm";
import UpdateBorrowerProfileDataForm from "../../../forms/UpdateBorrowerProfileDataForm";
import UpdateBorrowerDocumentMetadataForm from "../../../forms/UpdateBorrowerDocumentMetadataForm";
import CreateEditBorrowerEntityForm from "../../../forms/CreateEditBorrowerEntityForm";
import CreateEditBorrowerExperienceRecordForm from "../../../forms/CreateEditBorrowerExperienceRecordForm";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import UploadProfileDocumentsForm from "../../../forms/UploadProfileDocumentsForm";
import { isNil } from "lodash";

/**
 * DashboardSidebar - Stateless sidebar shell component
 * Handles the sidebar UI, resizing, animations, and backdrop
 * Content is managed by DashboardSidebarContent
 */
const DashboardSidebar = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sidebar = useSelector((state) => state.sidebar);
  const rootSx = useSelector((state) => state.sidebar?.sx?.rootSx);
  const headerSx = useSelector((state) => state.sidebar?.sx?.headerSx);
  const bodySx = useSelector((state) => state.sidebar?.sx?.bodySx);
  const { isOpen, type } = sidebar;

  const [width, setWidth] = useState(isMobile ? "100%" : 680);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const minWidth = 320;
  const maxWidth = isMobile ? "100%" : 1200;
  const pxSize = 2;
  // const pxSize = isMobile ? 2.4 : 3.4;
  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        dispatch(sidebarClose());
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, dispatch]);

  const handleClose = () => {
    dispatch(sidebarClose());
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <Fade in={isOpen}>
        <Box
          onClick={handleClose}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1200,
          }}
        />
      </Fade>

      {/* Sidebar */}
      <Fade in={isOpen}>
        <Paper
          ref={sidebarRef}
          elevation={8}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: `${width}px`,
            zIndex: 1300,
            display: "flex",
            // borderTopLeftRadius: "10px",
            // borderBottomLeftRadius: "10px",
            borderTopRightRadius: "0px",
            borderBottomRightRadius: "0px",
            flexDirection: "column",
            overflow: "hidden",
            ...rootSx,
          }}
        >
          {/* Resize Handle with Thumb */}
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "8px",
              cursor: "ew-resize",
              backgroundColor: "transparent",
              display: "flex",

              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                opacity: 1,
                backgroundColor: grey[100],
                ".resize-thumb": {
                  opacity: 1,
                  backgroundColor: grey[400],
                },
              },
              "&:active .resize-thumb": {
                backgroundColor: theme.palette.primary.main,
              },
              transition: "background-color 0.2s",
              zIndex: 1,
              ...headerSx,
            }}
          >
            <Box
              className="resize-thumb"
              sx={{
                width: "2px",
                height: "40px",
                backgroundColor: grey[400],
                // border: `1px solid ${grey[600]}`,
                borderRadius: "4px",
                opacity: 0.5,
                transition: "all 0.2s ease",
                pointerEvents: "none",
              }}
            />

            {/* Resize Thumb Indicator */}
          </Box>

          {/* Header */}
          <Box
            sx={{
              p: 2,
              px: pxSize,
              borderBottom: 1,
              borderColor: "#eeeeee",
              // borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Flx column>
              <Htag h1 bold>
                {getSidebarTitle(type, sidebar)}
              </Htag>
              {isNil(getSidebarDescription(type, sidebar)) ? null : (
                <Txt sx={{ fontSize: "12px", color: grey[600], mt: 0.5 }}>
                  {getSidebarDescription(type, sidebar)}
                </Txt>
              )}
            </Flx>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              // p: 3,
              p: 2,
              height: "100%",
              px: pxSize,
              overflowY: "auto",
              overflowX: "hidden",
              ...bodySx,
            }}
          >
            <DashboardSidebarContent />
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

const dynamicStyles = {};

export const DASHBOARD_SIDEBAR_TYPES = {
  ADD_DOCUMENT: "ADD_DOCUMENT",
  EDIT_BORROWER_PROFILE: "EDIT_BORROWER_PROFILE",
  ADD_EXPERIENCE: "ADD_EXPERIENCE",
  EDIT_EXPERIENCE: "EDIT_EXPERIENCE",
  ADD_ENTITY: "ADD_ENTITY",
  EDIT_ENTITY: "EDIT_ENTITY",
  EDIT_BORROWER_DOCUMENT_METADATA: "EDIT_BORROWER_DOCUMENT_METADATA",
  UPDATE_LOAN_CONDITION: "UPDATE_LOAN_CONDITION",
  ADD_BORROWER_BANK_INFO: "ADD_BORROWER_BANK_INFO",
  EDIT_BORROWER_BANK_INFO: "EDIT_BORROWER_BANK_INFO",
};

// Helper function to get sidebar title based on type
const getSidebarTitle = (type, sidebar) => {
  const titles = {
    [DASHBOARD_SIDEBAR_TYPES.ADD_DOCUMENT]:
      sidebar.metadata?.title || "Upload Document",
    [DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_PROFILE]: "Edit Profile",
    [DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_DOCUMENT_METADATA]: "Edit Document",
    [DASHBOARD_SIDEBAR_TYPES.ADD_ENTITY]: "Add Entity",
    [DASHBOARD_SIDEBAR_TYPES.EDIT_ENTITY]: "Edit Entity",
    [DASHBOARD_SIDEBAR_TYPES.ADD_EXPERIENCE]: "Add Experience",
    [DASHBOARD_SIDEBAR_TYPES.EDIT_EXPERIENCE]: "Edit Experience",
    [DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION]: "Action Loan Condition",
    [DASHBOARD_SIDEBAR_TYPES.ADD_BORROWER_BANK_INFO]: "Add Bank Info",
    [DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_BANK_INFO]: "Edit Bank Info",
  };
  return titles[type] || "Sidebar";
};
const getSidebarDescription = (type, sidebar) => {
  const descriptions = {
    [DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION]:
      "Please provide a response to the below condition and/or upload documents to address the open item.",
  };
  return descriptions[type] || null;
};

const DashboardSidebarContent = () => {
  const { type, data, metadata } = useSelector((state) => state.sidebar);

  // Route to the appropriate content component based on type
  switch (type) {
    case DASHBOARD_SIDEBAR_TYPES.ADD_DOCUMENT:
      return <UploadProfileDocumentsForm data={data} metadata={metadata} />;

    case DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_BANK_INFO:
      return (
        <CreateEditBorrowerBankInfoForm
          isEditing
          initialValues={data}
          metadata={metadata}
        />
      );

    case DASHBOARD_SIDEBAR_TYPES.ADD_BORROWER_BANK_INFO:
      return <CreateEditBorrowerBankInfoForm data={data} metadata={metadata} />;

    case DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION:
      return <UpdateLoanConditionForm data={data} metadata={metadata} />;

    case DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_PROFILE:
      return <UpdateBorrowerProfileDataForm metadata={metadata} />;

    case DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_DOCUMENT_METADATA:
      return (
        <UpdateBorrowerDocumentMetadataForm file={data} metadata={metadata} />
      );

    case DASHBOARD_SIDEBAR_TYPES.ADD_ENTITY:
      return <CreateEditBorrowerEntityForm data={data} metadata={metadata} />;

    case DASHBOARD_SIDEBAR_TYPES.EDIT_ENTITY:
      return (
        <CreateEditBorrowerEntityForm
          isEditing
          initialValues={data}
          metadata={metadata}
        />
      );

    case DASHBOARD_SIDEBAR_TYPES.ADD_EXPERIENCE:
      return (
        <CreateEditBorrowerExperienceRecordForm
          data={data}
          metadata={metadata}
        />
      );

    case DASHBOARD_SIDEBAR_TYPES.EDIT_EXPERIENCE:
      return (
        <CreateEditBorrowerExperienceRecordForm
          isEditing
          initialValues={data}
          metadata={metadata}
        />
      );

    default:
      return <DefaultContent />;
  }
};

// Default content when no type is specified
const DefaultContent = () => {
  return (
    <Flx column center sx={{ p: 4 }}>
      <Txt secondary>No content type specified</Txt>
    </Flx>
  );
};

export default DashboardSidebar;
